import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle, ChevronRight } from 'lucide-react';
import { brands } from '../mock';

export const BrandsPage = () => {
  const [selectedBrand, setSelectedBrand] = useState(brands[0]);

  // Categories for each brand (mock data - will be replaced with actual categories later)
  const brandCategories = {
    "Denon": ["AV Receivers", "Integrated Amplifiers", "Network Players", "CD Players"],
    "Marantz": ["AV Receivers", "Integrated Amplifiers", "CD Players", "Turntables"],
    "XTZ": ["Tower Speakers", "Bookshelf Speakers", "Subwoofers", "Center Speakers"],
    "Epson": ["4K Projectors", "Laser Projectors", "Home Cinema", "Short Throw"],
    "Pulz": ["Wireless Speakers", "Soundbars", "Bluetooth Speakers", "Multi-Room"],
    "AudioControl": ["Amplifiers", "Processors", "Equalizers", "Distribution"],
    "ARCAM": ["Integrated Amps", "AV Receivers", "Streamers", "CD Players"],
    "IOTAVX": ["Integrated Amplifiers", "Power Amplifiers", "Pre-Amplifiers", "DACs"],
    "Monoprice": ["Cables & Accessories", "Mounts", "In-Wall Speakers", "Audio Components"],
    "Optoma": ["4K Projectors", "Gaming Projectors", "Home Theater", "Ultra Short Throw"]
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-b from-zinc-950 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-10">
          <Badge className="mb-6 bg-lime-600/20 text-lime-500 border-lime-600/50">
            Premium Partners
          </Badge>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight max-w-4xl">
            Wide Range of
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-lime-600 mt-2">
              Finest Brands
            </span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl leading-relaxed">
            Explore our authorized partnerships with world-class audio-visual brands. 
            Select a brand to discover their complete range of products and solutions.
          </p>
        </div>
      </section>

      {/* Interactive Brand Browser */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Sidebar - Brand List */}
            <div className="lg:col-span-4 xl:col-span-3">
              <div className="lg:sticky lg:top-24 space-y-3">
                <h2 className="text-xl font-bold text-white mb-6">Select Brand</h2>
                {brands.map((brand, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedBrand(brand)}
                    onMouseEnter={() => setSelectedBrand(brand)}
                    className={`w-full text-left p-4 rounded-xl transition-all duration-300 ${
                      selectedBrand.name === brand.name
                        ? 'bg-lime-600/20 border-2 border-lime-600 shadow-lg shadow-lime-600/20'
                        : 'bg-zinc-900/50 border-2 border-zinc-800 hover:border-lime-600/50 hover:bg-zinc-900'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-white font-semibold mb-1">
                          {brand.name}
                        </div>
                        <div className={`text-sm ${
                          selectedBrand.name === brand.name ? 'text-lime-500' : 'text-gray-500'
                        }`}>
                          {brand.category}
                        </div>
                      </div>
                      <ChevronRight className={`w-5 h-5 transition-all duration-300 ${
                        selectedBrand.name === brand.name ? 'text-lime-500 translate-x-1' : 'text-gray-600'
                      }`} />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right Content Panel */}
            <div className="lg:col-span-8 xl:col-span-9">
              <Card className="bg-zinc-900/30 border-lime-900/20 backdrop-blur-sm shadow-xl">
                <CardContent className="p-0">
                  {/* Brand Header */}
                  <div className="p-8 sm:p-12 border-b border-lime-900/20">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
                      <div className="flex-1">
                        <Badge className="mb-4 bg-lime-600/20 text-lime-500 border-lime-600/50">
                          Authorized Partner
                        </Badge>
                        <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                          {selectedBrand.name}
                        </h2>
                        <p className="text-gray-400 text-lg">
                          {selectedBrand.category}
                        </p>
                      </div>
                      
                      {/* Brand Logo */}
                      <div className="w-full md:w-64 h-28 bg-black rounded-xl flex items-center justify-center p-6">
                        <img
                          src={selectedBrand.logo}
                          alt={selectedBrand.name}
                          className="max-w-full max-h-full object-contain"
                          style={{
                            filter: 'brightness(0) invert(1)',
                          }}
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Categories Section */}
                  <div className="p-8 sm:p-12">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      Explore {selectedBrand.name}
                    </h3>
                    <p className="text-gray-400 mb-8">
                      Discover our {selectedBrand.name} collection organized by categories and series
                    </p>

                    <h4 className="text-xl font-semibold text-white mb-6">
                      Browse {selectedBrand.name} Collections
                    </h4>

                    {/* Category Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
                      {(brandCategories[selectedBrand.name] || ["Products", "Solutions", "Systems", "Accessories"]).map((category, index) => (
                        <Card key={index} className="bg-zinc-800/30 border-lime-900/20 hover:border-lime-600/50 transition-all duration-300 group cursor-pointer">
                          <CardContent className="p-6">
                            <div className="flex items-start space-x-4">
                              <div className="w-12 h-12 bg-lime-600/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-lime-600/30 transition-colors duration-300">
                                <CheckCircle className="w-6 h-6 text-lime-500" />
                              </div>
                              <div className="flex-1">
                                <h5 className="text-white font-semibold mb-2 group-hover:text-lime-500 transition-colors duration-300">
                                  {category}
                                </h5>
                                <p className="text-gray-500 text-sm mb-3">
                                  Explore our range of {category.toLowerCase()}
                                </p>
                                <div className="flex items-center text-lime-500 text-sm font-medium group-hover:translate-x-1 transition-transform duration-300">
                                  View Collection
                                  <ChevronRight className="w-4 h-4 ml-1" />
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link to="/contact" className="flex-1">
                        <Button size="lg" className="w-full bg-lime-600 hover:bg-lime-700 text-white transition-all duration-300 shadow-lg shadow-lime-600/30">
                          View All {selectedBrand.name} Products
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </Link>
                      <Link to="/contact" className="flex-1">
                        <Button size="lg" variant="outline" className="w-full border-2 border-lime-600 text-lime-500 hover:bg-lime-600/10 transition-all duration-300">
                          Request Quote
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-zinc-950 border-t border-lime-900/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-zinc-900/50 border-lime-900/20 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl font-bold text-white mb-6">
                Why Authorized Partnership Matters
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                <div>
                  <div className="w-12 h-12 bg-lime-600/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-lime-500" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Genuine Products</h3>
                  <p className="text-gray-400 text-sm">100% authentic products with manufacturer warranties</p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-lime-600/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-lime-500" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Expert Support</h3>
                  <p className="text-gray-400 text-sm">Manufacturer-backed technical support and service</p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-lime-600/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-lime-500" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Best Pricing</h3>
                  <p className="text-gray-400 text-sm">Competitive pricing with exclusive partner benefits</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-lime-900/20 via-lime-800/10 to-lime-900/20 border-y border-lime-900/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Experience Premium Quality?
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Get expert recommendations on the best brands and products for your home cinema
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-lime-600 hover:bg-lime-700 text-white px-10 py-6 text-lg shadow-lg shadow-lime-600/30">
              Schedule Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
